import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceDot } from "recharts";
import { TestResponse } from "@/types/test-response";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { calculateScorePercentage } from "@/utils/score-utils";

interface PercentileChartProps {
  response: TestResponse;
}

interface CandidateScoreData {
  candidateIndex: number;
  candidateId: string;
  aiScore: number;
  integrityScore: number;
  isCurrentCandidate: boolean;
  candidateName?: string;
}

export function PercentileChart({ response }: PercentileChartProps) {
  // Fetch all test responses for this test
  const { data: allResponses, isLoading } = useQuery({
    queryKey: ['test-responses-scores', response.test.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('test_responses')
        .select(`
          id,
          candidate_id,
          score,
          integrity_score,
          status,
          question_responses!inner(
            id,
            question_id,
            score,
            ai_feedback
          ),
          test:tests(
            id,
            questions!fk_test(
              id,
              marks
            )
          ),
          candidate:profiles(
            full_name
          )
        `)
        .eq('test_id', response.test.id)
        .eq('status', 'completed');

      if (error) throw error;
      return data;
    },
    enabled: !!response.test.id,
  });

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Candidate Score Distribution</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-64 flex items-center justify-center">
            <div className="text-muted-foreground">Loading candidate scores...</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!allResponses || allResponses.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Candidate Score Distribution</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-64 flex items-center justify-center">
            <div className="text-muted-foreground">No completed responses available</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Process all responses to get score data
  const candidateData: CandidateScoreData[] = allResponses.map((resp: any, index) => {
    // Use stored total AI score as percentage
    const aiScorePercentage = resp.total_ai_score || 0;
    const integrityScore = resp.integrity_score || 0;

    return {
      candidateIndex: index + 1,
      candidateId: resp.candidate_id,
      aiScore: aiScorePercentage,
      integrityScore,
      isCurrentCandidate: resp.candidate_id === response.candidate_id,
      candidateName: resp.candidate?.full_name,
    };
  });

  // Sort by AI score for better visualization
  candidateData.sort((a, b) => a.aiScore - b.aiScore);
  
  // Re-index after sorting
  candidateData.forEach((item, index) => {
    item.candidateIndex = index + 1;
  });

  // If there's only one candidate, add dummy data points for line visualization
  if (candidateData.length === 1) {
    const current = candidateData[0];
    candidateData.unshift({
      candidateIndex: 0,
      candidateId: 'dummy-start',
      aiScore: Math.max(0, current.aiScore - 10),
      integrityScore: Math.max(0, current.integrityScore - 10),
      isCurrentCandidate: false,
    });
    candidateData.push({
      candidateIndex: 2,
      candidateId: 'dummy-end',
      aiScore: Math.min(100, current.aiScore + 10),
      integrityScore: Math.min(100, current.integrityScore + 10),
      isCurrentCandidate: false,
    });
    // Update the current candidate index
    current.candidateIndex = 1;
  }

  const currentCandidate = candidateData.find(c => c.isCurrentCandidate);

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0]?.payload;
      // Only show tooltip for the current candidate
      if (data?.isCurrentCandidate) {
        return (
          <div className="bg-background border border-border rounded-lg p-3 shadow-lg">
            <p className="text-sm font-medium">
              {data?.isCurrentCandidate ? response.candidate?.full_name || "Current Candidate" : `Candidate ${label}`}
            </p>
            {payload.map((entry: any, index: number) => (
              <p key={index} className="text-sm" style={{ color: entry.color }}>
                {`${entry.name}: ${Math.round(entry.value)}%`}
              </p>
            ))}
          </div>
        );
      }
    }
    return null;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Candidate Score Distribution</CardTitle>
        <p className="text-sm text-muted-foreground">
          AI and Integrity scores across all candidates
        </p>
      </CardHeader>
      <CardContent>
        <div className="h-64 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={candidateData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--muted-foreground) / 0.2)" />
              <XAxis 
                dataKey="candidateIndex" 
                stroke="hsl(var(--muted-foreground))"
                fontSize={12}
                tick={false}
                axisLine={true}
              />
              <YAxis 
                stroke="hsl(var(--muted-foreground))"
                fontSize={12}
                domain={[0, 100]}
                tickFormatter={(value) => `${value}%`}
              />
              <Tooltip content={<CustomTooltip />} />
              
              {/* AI Score Line - Green */}
              <Line 
                type="monotone" 
                dataKey="aiScore" 
                stroke="#22c55e"
                strokeWidth={3}
                dot={(props) => {
                  const { payload } = props;
                  if (payload?.candidateId?.startsWith('dummy')) {
                    return null;
                  }
                  if (payload?.isCurrentCandidate) {
                    return <circle {...props} r={8} fill="#22c55e" stroke="white" strokeWidth={3} />;
                  }
                  return <circle {...props} r={1.5} fill="#22c55e" stroke="white" strokeWidth={0.5} />;
                }}
                name="AI Score"
                connectNulls={false}
              />
              
              {/* Integrity Score Line - Blue */}
              <Line 
                type="monotone" 
                dataKey="integrityScore" 
                stroke="#3b82f6"
                strokeWidth={3}
                dot={(props) => {
                  const { payload } = props;
                  if (payload?.candidateId?.startsWith('dummy')) {
                    return null;
                  }
                  if (payload?.isCurrentCandidate) {
                    return <circle {...props} r={8} fill="#3b82f6" stroke="white" strokeWidth={3} />;
                  }
                  return <circle {...props} r={1.5} fill="#3b82f6" stroke="white" strokeWidth={0.5} />;
                }}
                name="Integrity Score"
                connectNulls={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
        
        {/* Legend and current candidate info */}
        <div className="mt-4 space-y-2">
          <div className="flex justify-center gap-6 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-green-500"></div>
              <span>AI Score</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-blue-500"></div>
              <span>Integrity Score</span>
            </div>
          </div>
          
          {currentCandidate && (
            <div className="text-center text-sm text-muted-foreground">
              Candidate position: AI Score {currentCandidate.aiScore}% • Integrity Score {currentCandidate.integrityScore}%
              <br />
              <span className="text-xs">
                Candidate {currentCandidate.candidateIndex} of {allResponses.length}
              </span>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}